import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { compressImage, getPosition } from "@/lib/helpers";
import { t } from "@/lib/i18n";

const TYPES = ["delay", "breakdown", "damage", "refused", "other"];

export function IssueDialog({ orderId, lang, onClose, onSent }) {
  const [type, setType] = useState("delay");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const [pos, ...compressed] = await Promise.all([getPosition(), ...files.map((f) => compressImage(f))]);
      const fd = new FormData();
      fd.append("type", type);
      fd.append("note", note);
      if (pos) {
        fd.append("lat", pos.lat);
        fd.append("lng", pos.lng);
      }
      compressed.forEach((f) => fd.append("files", f));
      await api.post(`/driver/orders/${orderId}/issues`, fd);
      toast.success(t(lang, "issue_sent"));
      onSent();
    } catch (e) {
      toast.error(errMsg(e, t(lang, "error")));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md border-white/10 bg-[#111827]" data-testid="issue-dialog">
        <DialogTitle className="font-display text-2xl uppercase text-red-300">{t(lang, "report_issue")}</DialogTitle>
        <div className="eyebrow">{t(lang, "issue_type")}</div>
        <div className="grid grid-cols-2 gap-2">
          {TYPES.map((k) => (
            <button key={k} type="button" onClick={() => setType(k)} data-testid={`issue-type-${k}`}
              className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${type === k ? "border-red-500 bg-red-500/15 text-red-200" : "border-white/10 text-slate-300"}`}>
              {t(lang, `issue_${k}`)}
            </button>
          ))}
        </div>
        <Textarea rows={3} placeholder={t(lang, "note_ph")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="issue-note-input" />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
          <Paperclip className="h-4 w-4" /> {t(lang, "attach_photo")} {files.length > 0 && `(${files.length})`}
          <input type="file" accept="image/*" multiple hidden data-testid="issue-file-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} data-testid="issue-cancel-btn" className="h-12 flex-1 rounded-xl border border-white/10">{t(lang, "cancel")}</button>
          <button type="button" onClick={send} disabled={busy} data-testid="issue-send-btn" className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 font-semibold text-white disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t(lang, "send")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
